/**
 * Detects verbal intents from a user transcript so Glitch can act on them.
 * Currently handles:
 *   - Image generation: "draw / show / render / generate an image of X"
 *   - Video generation: "generate / render / make a video/clip of X"
 *   - Voice note save:  "save that to ezScribe / ezBuildabot / notes"
 *
 * Each detector returns {matched:boolean, ...payload}.
 */

const NOTE_PROJECTS = ["ezScribe", "ezBuildabot", "general"];

const cleanSubject = (s) =>
  (s || "")
    .trim()
    .replace(/[.!?…,;:"']+$/, "")
    .replace(/^(please\s+)?(a|an|the)\s+/i, "")
    .replace(/^(of|about)\s+/i, "")
    .trim();

export const detectImageIntent = (text) => {
  if (!text) return { matched: false };
  // "draw me a blue orb", "generate an image of a sunset", "render a picture of..."
  const re =
    /\b(?:draw|sketch|show|render|generate|create|make|paint|design)\s+(?:me\s+)?(?:up\s+)?(?:a|an|some)?\s*(?:image|picture|photo|photograph|drawing|artwork|art|illustration|painting|pic)?\s*(?:of|showing|depicting|with)?\s+(.{3,})/i;
  const m = text.match(re);
  if (!m) return { matched: false };
  // Only trigger when the sentence actually mentions image or draw/paint keyword pair
  const requiresPic =
    /\b(image|picture|photo|photograph|drawing|artwork|art|illustration|painting|pic)\b/i.test(
      text
    );
  const requiresDrawVerb = /\b(draw|sketch|paint|design)\b/i.test(text);
  if (!requiresPic && !requiresDrawVerb) return { matched: false };
  const subject = cleanSubject(m[1]);
  if (subject.length < 3) return { matched: false };
  return { matched: true, prompt: subject };
};

export const detectVideoIntent = (text) => {
  if (!text) return { matched: false };
  // Accept an optional duration phrase between article and the video keyword:
  //   "make a short video", "render a 5 second clip", "an 8s reel of..."
  const re =
    /\b(?:generate|make|create|render|film|produce|shoot)\s+(?:me\s+)?(?:a|an)?\s*(?:short\s+)?(?:\d{1,2}\s*(?:s|sec|secs|second|seconds)\s+)?(?:video|clip|movie|film|animation|reel)\s+(?:of|about|showing|depicting)?\s+(.{3,})/i;
  const m = text.match(re);
  if (!m) return { matched: false };
  const subject = cleanSubject(m[1]);
  // Duration parsing: capture N or NN seconds anywhere in the sentence
  const durMatch = text.match(/\b(\d{1,2})\s*(?:s|sec|secs|second|seconds)\b/i);
  const duration = durMatch
    ? Math.max(4, Math.min(8, parseInt(durMatch[1], 10)))
    : 6;
  if (subject.length < 3) return { matched: false };
  return { matched: true, prompt: subject, duration };
};

export const detectSaveNoteIntent = (text) => {
  if (!text) return { matched: false };
  // "save that to ezScribe" / "note this for ezBuildabot" / "add that to my notes"
  const re =
    /\b(?:save|note|add|drop|pin|remember)\s+(?:that|this|it|last|the\s+last)?\s*(?:to|into|for|as\s+a\s+note\s+(?:to|for|in))\s+(?:my\s+)?(ezScribe|ezBuildabot|general(?:\s+notes)?|notes?)\b/i;
  const m = text.match(re);
  if (!m) return { matched: false };
  let raw = m[1].toLowerCase();
  let project = "general";
  if (raw.startsWith("ezscribe")) project = "ezScribe";
  else if (raw.startsWith("ezbuildabot")) project = "ezBuildabot";
  return { matched: true, project };
};

const cleanMemoryContent = (s) =>
  (s || "")
    .trim()
    .replace(/[.!?…,;:"']+$/, "")
    .replace(/^(please\s+|hey\s+glitch,?\s+)+/i, "")
    .trim();

export const detectMemoryIntent = (text) => {
  if (!text) return { matched: false };

  // 1. "remember (that) X" / "remember that my name is X"
  const rememberRe =
    /\b(?:remember|memorize|note\s+down)\s+(?:that|this\s+fact\s+that)?\s*(.{3,})/i;
  // 2. "add to memory that X" / "save to memory that X" / "store in memory X"
  const explicitRe =
    /\b(?:add|save|store|put|write)\s+(?:this|that|the\s+following|for\s+later)?\s*(?:to|into|in)\s+(?:my\s+)?memory\s+(?:that\s+)?(.{3,})/i;

  const m1 = text.match(explicitRe);
  if (m1) {
    const content = cleanMemoryContent(m1[1]);
    if (content.length >= 3) return { matched: true, content };
  }

  // 3. "X. Save that/this to memory" – X is captured content before trigger
  const trailingRe =
    /^(.+?)[\s,.]+(?:save|add|store|put|note)\s+(?:this|that|it|the\s+last(?:\s+thing)?)?\s*(?:to|into|in)\s+(?:my\s+)?memory\b/i;
  const m2 = text.match(trailingRe);
  if (m2) {
    const content = cleanMemoryContent(m2[1]);
    if (content.length >= 3) return { matched: true, content };
  }

  // 4. Bare reference: "save this to memory" – no captured content; caller
  //    should fall back to the last assistant message.
  const bareRe =
    /\b(?:save|add|store|put|note)\s+(?:this|that|it|the\s+last(?:\s+thing)?)?\s*(?:to|into|in)\s+(?:my\s+)?memory\b/i;
  if (bareRe.test(text)) {
    return { matched: true, content: null, useLastAssistant: true };
  }

  // 5. "remember X" – catches the general case last so it doesn't shadow the
  //    "remember that to save to notes" style patterns above.
  const m3 = text.match(rememberRe);
  if (m3) {
    const content = cleanMemoryContent(m3[1]);
    // Skip if the trailing content is really "to save to notes / ezScribe" etc.
    if (
      content.length >= 3 &&
      !/^(to\s+(?:save|add|put)|that\s+i\s+said)/i.test(content)
    ) {
      return { matched: true, content };
    }
  }

  return { matched: false };
};

export const availableProjects = NOTE_PROJECTS;
